import React, { useMemo, useState } from 'react';

/**
 * Sidebar listing saved conversations for the selected company.
 */
export default function ResearchConversationSidebar({
  conversations,
  activeConversationId,
  busy,
  onSelect,
  onCreateNew,
  onRename,
  onDelete,
}) {
  const [editingId, setEditingId] = useState(null);
  const [draftTitle, setDraftTitle] = useState('');

  const sorted = useMemo(
    () => [...(conversations || [])].sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || '')),
    [conversations],
  );

  const beginRename = (conv) => {
    setEditingId(conv.conversation_id);
    setDraftTitle(conv.title || '');
  };

  const cancelRename = () => {
    setEditingId(null);
    setDraftTitle('');
  };

  const commitRename = async () => {
    const title = draftTitle.trim();
    if (!title || !editingId) return;
    try {
      await onRename(editingId, title);
      cancelRename();
    } catch (err) {
      console.error('Failed to rename conversation:', err);
    }
  };

  return (
    <div className="border-end bg-white d-flex flex-column" style={{ width: 320, minWidth: 260, maxWidth: 360 }}>
      <div className="p-2 border-bottom d-flex align-items-center justify-content-between">
        <span className="fw-semibold small text-uppercase text-muted">Conversations</span>
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          onClick={onCreateNew}
          disabled={busy}
          title="Start a new conversation"
        >
          <i className="bi bi-plus-lg me-1" />New
        </button>
      </div>

      <div className="overflow-auto" style={{ height: 460 }}>
        {sorted.length === 0 && (
          <div className="p-3 text-muted small">
            No saved conversations yet.
          </div>
        )}

        {sorted.map((conv) => {
          const isActive = conv.conversation_id === activeConversationId;
          const isEditing = conv.conversation_id === editingId;
          return (
            <div
              key={conv.conversation_id}
              className={`border-bottom p-2 ${isActive ? 'bg-light' : ''}`}
            >
              {!isEditing && (
                <>
                  <button
                    type="button"
                    className="btn btn-link text-start text-decoration-none p-0 w-100"
                    onClick={() => onSelect(conv.conversation_id)}
                    disabled={busy}
                    title={conv.title}
                  >
                    <div className="fw-semibold text-dark small text-truncate">{conv.title || 'Untitled conversation'}</div>
                    <div className="text-muted" style={{ fontSize: '0.74rem' }}>
                      {conv.message_count || 0} message{(conv.message_count || 0) === 1 ? '' : 's'}
                    </div>
                  </button>
                  <div className="mt-1 d-flex gap-1">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => beginRename(conv)}
                      disabled={busy}
                    >
                      Rename
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={async () => {
                        if (window.confirm('Delete this conversation?')) {
                          try {
                            await onDelete(conv.conversation_id);
                          } catch (err) {
                            console.error('Failed to delete conversation:', err);
                          }
                        }
                      }}
                      disabled={busy}
                    >
                      Delete
                    </button>
                  </div>
                </>
              )}

              {isEditing && (
                <div className="d-flex flex-column gap-2">
                  <input
                    className="form-control form-control-sm"
                    value={draftTitle}
                    onChange={(e) => setDraftTitle(e.target.value)}
                    maxLength={120}
                    autoFocus
                  />
                  <div className="d-flex gap-1">
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={commitRename}
                      disabled={!draftTitle.trim() || busy}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={cancelRename}
                      disabled={busy}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
